import WaitingDots from "components/WaitingDots";
import { Account } from "models/Account";
import { LocaleCatalogue } from "models/Locale";
import { Person } from "models/Person";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { Navigate, Route, Routes } from "react-router-dom";
import { Alert } from "reactstrap";
import { bindActionCreators } from "redux";
import { getAccountLocale, getPersonLoggedIn, updatePerson } from 'services/AccountApi';
import { syncEnabledAccountWebPushSubscriptions } from "services/WebPush";
import { RootStateAccount } from "storeAccount/accountStore";
import { getAuthAccounts } from "storeAccount/actions/Customers";
import { AuthenticationState } from "../../storeAccount/reducers/Authentication";
import AccountMenu from "./AccountMenu";
import AccountHome from "./AccountHome";
import AccountSettingsPage from "./AccountSettingsPage";
import AccountAssets from "./AccountAssets";
import AccountInvoiceList from "./InvoiceQuoteViews/AccountInvoiceList";
import {
    AccountTranslationProvider,
    getAccountTranslation,
    normalizeAccountLocale,
    syncBrowserAccountLocale,
} from "./translations";
import { getThemeApplicationUrl, ThemeLogo, useTheme } from "theme";

const AccountProjects = React.lazy(() => import("./Projects/AccountProjects"));
const AccountProjectContracts = React.lazy(() => import("./Projects/AccountProjectContracts"));
const AccountResponsibleContracts = React.lazy(() => import("./Projects/AccountResponsibleContracts"));

interface Props {
    authentication: AuthenticationState;
}

interface Actions {
    getAuthAccounts: typeof getAuthAccounts;
}

export const AccountMainPage: React.FC<Props & Actions> = ({
    authentication,
    getAuthAccounts
}) => {
    const theme = useTheme();

    const [customer, setCustomer] = useState<Account | null>(null)
    const [customers, setCustomers] = useState<Account[]>([])
    const [person, setPerson] = useState<Person | null>(null)
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
    const [localeCatalogue, setLocaleCatalogue] = useState<LocaleCatalogue | null>(null)
    const [localeError, setLocaleError] = useState(false)
    const [localeReload, setLocaleReload] = useState(0)
    const mobileMenuToggleRef = useRef<HTMLButtonElement>(null)
    const mobileMenuCloseRef = useRef<HTMLButtonElement>(null)

    const closeMobileMenu = useCallback(() => {
        setMobileMenuOpen(false)
        mobileMenuToggleRef.current?.focus()
    }, [])

    const refreshCustomers = useCallback(() => {
        if (authentication === "") {
            return Promise.resolve();
        }
        return getAuthAccounts().then((nextCustomers: Account[]) => {
            setCustomers(nextCustomers);
            setCustomer(current =>
                nextCustomers.find(candidate => candidate.id === current?.id) ?? nextCustomers[0] ?? null
            );
        });
    }, [authentication, getAuthAccounts]);

    const updateCustomer = useCallback((updated: Account) => {
        setCustomers(current => current.map(candidate => candidate.id === updated.id ? updated : candidate));
        setCustomer(current => current?.id === updated.id ? updated : current);
    }, []);

    useEffect(() => {

        if (authentication === "") {
            const payload = typeof window !== "undefined" && window.btoa(JSON.stringify({ redirect_url: `${window.location.origin}/auth-return` }));
            window.location.assign(getThemeApplicationUrl(theme, "auth", `/entry/${payload}`))
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [theme])

    useEffect(() => {
        if (authentication !== "") {
            refreshCustomers();

            getPersonLoggedIn().then(async (person) => {
                try {
                    setPerson(await syncBrowserAccountLocale(person, updatePerson));
                } catch {
                    setPerson(person);
                }
            })
        }

    }, [authentication, refreshCustomers])

    useEffect(() => {
        if (authentication === "") {
            return;
        }
        const refreshOnFocus = () => {
            refreshCustomers().catch(() => undefined);
        };
        window.addEventListener("focus", refreshOnFocus);
        return () => window.removeEventListener("focus", refreshOnFocus);
    }, [authentication, refreshCustomers]);

    useEffect(() => {
        if (!person) {
            return;
        }
        let active = true;
        setLocaleCatalogue(null);
        setLocaleError(false);
        getAccountLocale(normalizeAccountLocale(person.locale))
            .then(catalogue => {
                if (active) setLocaleCatalogue(catalogue);
            })
            .catch(() => {
                if (active) setLocaleError(true);
            });
        return () => {
            active = false;
        };
    }, [localeReload, person]);

    useEffect(() => {
        if (authentication === "") {
            return;
        }

        const syncWebPush = () => {
            if (document.visibilityState === "visible") {
                syncEnabledAccountWebPushSubscriptions().catch(() => undefined);
            }
        };

        syncWebPush();
        document.addEventListener("visibilitychange", syncWebPush);
        return () => document.removeEventListener("visibilitychange", syncWebPush);
    }, [authentication]);

    useEffect(() => {
        if (!mobileMenuOpen) {
            return
        }

        const previousOverflow = document.body.style.overflow
        const closeOnEscape = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                closeMobileMenu()
            }
        }

        document.body.style.overflow = "hidden"
        document.addEventListener("keydown", closeOnEscape)
        mobileMenuCloseRef.current?.focus()

        return () => {
            document.body.style.overflow = previousOverflow
            document.removeEventListener("keydown", closeOnEscape)
        }
    }, [closeMobileMenu, mobileMenuOpen])

    if (person === null) {
        return (<WaitingDots />)
    }

    if (localeCatalogue === null) {
        if (localeError) {
            return <div className="container py-5">
                <Alert color="danger">
                    The customer portal translations could not be loaded.
                    <button
                        className="btn btn-sm btn-outline-danger ms-3"
                        onClick={() => setLocaleReload(value => value + 1)}
                        type="button"
                    >
                        Retry
                    </button>
                </Alert>
            </div>;
        }
        return <WaitingDots />;
    }

    const customerTranslations = localeCatalogue.namespaces.customer ?? {};
    const { t } = getAccountTranslation(
        person.locale,
        customerTranslations,
        localeCatalogue.intl_locale,
    );
    const chooseAccount = <div className="portal-panel portal-compact-empty">
        <i className="mdi mdi-domain" />
        {t("chooseAccountFirst")}
    </div>;

    return (<AccountTranslationProvider
        locale={person.locale}
        intlLocale={localeCatalogue.intl_locale}
        translations={customerTranslations}
    >
        <div className="account-portal">
            <header className="account-mobile-header">
                <button
                    ref={mobileMenuToggleRef}
                    type="button"
                    className="account-menu-toggle"
                    aria-label={t("openMenu")}
                    aria-controls="account-navigation"
                    aria-expanded={mobileMenuOpen}
                    onClick={() => setMobileMenuOpen(true)}
                >
                    <i className="bx bx-menu" aria-hidden="true"></i>
                </button>
                <ThemeLogo variant="dark" className="account-mobile-logo" />
                <span className="account-mobile-header-spacer" aria-hidden="true"></span>
            </header>
            <div className="account-portal-shell">
                <aside
                    id="account-navigation"
                    className={`account-menu-drawer${mobileMenuOpen ? " account-menu-drawer--open" : ""}`}
                >
                    <div className="account-drawer-header">
                        <span className="account-drawer-title">{t("menu")}</span>
                        <button
                            ref={mobileMenuCloseRef}
                            type="button"
                            className="account-menu-close"
                            aria-label={t("close")}
                            onClick={closeMobileMenu}
                        >
                            <i className="bx bx-x" aria-hidden="true"></i>
                        </button>
                    </div>
                    <div className="account-sidebar-brand">
                        <ThemeLogo variant="dark" />
                    </div>
                    <AccountMenu currentCustomer={customer} customers={customers} person={person}
                        setCustomer={setCustomer} onNavigate={closeMobileMenu} />
                </aside>

                <main className="account-route-content">
                    <div className="account-page-content">
                        <Routes>
                                <Route key={"home"} path={"/"} element={
                                    customer ? <AccountHome customer={customer} person={person} /> : chooseAccount
                                } />
                                <Route key={"invoices"} path={"/invoices"}
                                    element={<AccountInvoiceList customer={customer} quotes={false}
                                        person={person} />} />
                                <Route key={"quotes"} path={"/quotes"}
                                    element={<AccountInvoiceList customer={customer} quotes={true}
                                        person={person} />} />
                                <Route key={"assets"} path={"/assets"} element={
                                    customer ? <AccountAssets customer={customer} /> : chooseAccount
                                } />
                                <Route key={"projects"} path={"/projects"} element={
                                    customer
                                        ? <React.Suspense fallback={<WaitingDots />}>
                                            <AccountProjects accountId={customer.id} />
                                        </React.Suspense>
                                        : chooseAccount
                                } />
                                <Route key={"project-contracts"} path={"/projects/:projectId/contracts"} element={
                                    customer
                                        ? <React.Suspense fallback={<WaitingDots />}>
                                            <AccountProjectContracts person={person} accountId={customer.id} />
                                        </React.Suspense>
                                        : chooseAccount
                                } />
                                <Route key={"responsible-contracts"} path={"/projects/contracts-to-sign"} element={
                                    customer
                                        ? <React.Suspense fallback={<WaitingDots />}>
                                            <AccountResponsibleContracts person={person} accountId={customer.id} />
                                        </React.Suspense>
                                        : chooseAccount
                                } />
                                <Route key={"my-details"} path={"/my-details"} element={
                                    customer ? <AccountSettingsPage customer={customer} person={person} setPerson={setPerson} onCustomerChange={updateCustomer} /> : chooseAccount
                                } />
                                <Route path="/company-details" element={<Navigate replace to="/my-details" />} />
                                <Route path="/settings" element={<Navigate replace to="/my-details" />} />
                                <Route path="/security" element={<Navigate replace to="/my-details" />} />
                                <Route path="/notifications" element={<Navigate replace to="/my-details" />} />
                                <Route path="*" element={<Navigate replace to="/" />} />
                        </Routes>
                    </div>
                </main>
                </div>
            <button
                type="button"
                className={`account-menu-backdrop${mobileMenuOpen ? " account-menu-backdrop--visible" : ""}`}
                aria-label={t("close")}
                tabIndex={mobileMenuOpen ? 0 : -1}
                onClick={closeMobileMenu}
            />
        </div>
    </AccountTranslationProvider>);
}

const mapStateToProps = (state: RootStateAccount) => {
    return {
        authentication: state.Authentication,
    };
};

const mapDispatchToProps = (dispatch: any) => {
    return bindActionCreators({ getAuthAccounts }, dispatch);
};

export default connect(mapStateToProps, mapDispatchToProps)(AccountMainPage);
